import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import $ from "jquery";
import moment from "moment";
import ExcelJS from "exceljs";
import DataTable from "datatables.net-bs5";
import { Box, Button, Menu, MenuItem, Paper, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "daterangepicker/daterangepicker.css";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import { domesticReportAPI } from "../services/domesticReportApi";
import { srDetailDumpAPI } from "../services/srDetailDumpApi";
import { useAdminDashboardFilters } from "../hooks/useAdminDashboardFilters";
import {
  getRoutePermissions,
  hasPermission,
  isDosAdminUser,
  isSystemAdminUser,
} from "../utils/auth";
import {
  DOMESTIC_REPORT_COLUMNS,
  buildDomesticReportColumns,
  collectRecommendedTatColumns,
  decorateReportRows,
  isReportDayColumn,
} from "./DomesticReports";
import {
  getDataTableExportRows,
  getDataTableSearchTerm,
  isDataTableFiltered,
} from "../utils/dataTableExportUtils";
import "./AdminDashboard.css";
import "./RaiseComplaint.css";
import "./UserManagement.css";
import "./DomesticReports.css";

/** Fixed columns exported to Excel (checklist questions appended dynamically). */
export const SR_DETAIL_DUMP_COLUMNS = [
  { key: "parentSrNo", label: "SR No" },
  { key: "childSrNumber", label: "Child SR No" },
  { key: "srCode", label: "Parent SR No" },
  { key: "srType", label: "SR Type" },
  { key: "complaintType", label: "Complaint Type" },
  { key: "srCategory", label: "SR Category" },
  { key: "srStatus", label: "SR Status" },
  { key: "currentActivity", label: "Current Activity" },
  { key: "currentApprover", label: "Current Approver" },
  { key: "currentLevel", label: "Current Level" },
  { key: "raisedBy", label: "Raised By" },
  { key: "raisedDate", label: "Raised Date" },
  { key: "lastUpdatedOn", label: "Last Updated On" },
  { key: "closedDate", label: "Closed Date" },
  { key: "closureReason", label: "Closure Reason" },
  { key: "cancellationReason", label: "Cancellation Reason" },
  { key: "customerCode", label: "Customer Code" },
  { key: "customerName", label: "Customer Name" },
  { key: "customerType", label: "Customer Type" },
  { key: "customerCategory", label: "Customer Category" },
  { key: "customerAddress", label: "Customer Address" },
  { key: "city", label: "City" },
  { key: "district", label: "District" },
  { key: "state", label: "State" },
  { key: "country", label: "Country" },
  { key: "pinCode", label: "PIN Code" },
  { key: "mobileNo", label: "Mobile No" },
  { key: "emailId", label: "Email ID" },
  { key: "gstNo", label: "GST No" },
  { key: "dealerCode", label: "Dealer Code" },
  { key: "dealerName", label: "Dealer Name" },
  { key: "distributorCode", label: "Distributor Code" },
  { key: "distributorName", label: "Distributor Name" },
  { key: "salesOfficer", label: "Sales Officer" },
  { key: "salesManager", label: "Sales Manager" },
  { key: "businessVertical", label: "Business Vertical" },
  { key: "market", label: "Market" },
  { key: "plant", label: "Plant" },
  { key: "salesOrganization", label: "Sales Organization" },
  { key: "division", label: "Division" },
  { key: "distributionChannel", label: "Distribution Channel" },
  { key: "companyCode", label: "Company Code" },
  { key: "productCode", label: "Product Code" },
  { key: "productDescription", label: "Product Description" },
  { key: "productCategory", label: "Product Category" },
  { key: "productGroup", label: "Product Group" },
  { key: "brand", label: "Brand" },
  { key: "shade", label: "Shade" },
  { key: "thickness", label: "Thickness" },
  { key: "size", label: "Size" },
  { key: "batchNo", label: "Batch No" },
  { key: "quantity", label: "Quantity" },
  { key: "unit", label: "Unit" },
  { key: "invoiceNumber", label: "Invoice No" },
  { key: "invoiceDate", label: "Invoice Date" },
  { key: "odnNo", label: "ODN No" },
  { key: "dispatchDate", label: "Dispatch Date" },
  { key: "complaintDescription", label: "Complaint Description" },
  { key: "customerObservation", label: "Customer Observation" },
  { key: "internalObservation", label: "Internal Observation" },
  { key: "rootCause", label: "Root Cause" },
  { key: "correctiveAction", label: "Corrective Action" },
  { key: "preventiveAction", label: "Preventive Action" },
  { key: "resolutionSummary", label: "Resolution Summary" },
  { key: "remarks", label: "Remarks" },
  { key: "commercialClosureType", label: "Commercial Closure Type" },
  { key: "srnReferenceNo", label: "SRN Reference No" },
  { key: "srnAmount", label: "SRN Amount" },
  { key: "srnQuantity", label: "SRN Quantity" },
  { key: "creditNoteNo", label: "Credit Note No" },
  { key: "creditNoteAmount", label: "Credit Note Amount" },
  { key: "creditNoteQuantity", label: "Credit Note Quantity" },
  { key: "replacementType", label: "Replacement Type" },
  { key: "replacementOrderNo", label: "Replacement Order No" },
  { key: "reBillOrderNo", label: "Re-Bill Order No" },
  { key: "debitNoteNo", label: "Debit Note No" },
  { key: "debitNoteAmount", label: "Debit Note Amount" },
  { key: "createdOn", label: "Created On" },
  { key: "assignedOn", label: "Assigned On" },
  { key: "firstResponseDate", label: "First Response Date" },
  { key: "closedOn", label: "Closed On" },
  { key: "actualTat", label: "Actual TAT" },
  { key: "ageing", label: "Overall TAT" },
  { key: "slaStatus", label: "SLA Status" },
  { key: "totalQueryCount", label: "Total Query Count" },
  { key: "totalReassignCount", label: "Total Reassign Count" },
  { key: "finalApprover", label: "Final Approver" },
  { key: "lastActionBy", label: "Last Action By" },
  { key: "lastActionDate", label: "Last Action Date" },
  { key: "approvalStatus", label: "Approval Status" },
  { key: "approvalRemarks", label: "Approval Remarks" },
  { key: "totalQueries", label: "Total Queries" },
  { key: "lastQueryBy", label: "Last Query By" },
  { key: "lastQueryDate", label: "Last Query Date" },
  { key: "lastQueryRemarks", label: "Last Query Remarks" },
  { key: "closureType", label: "Closure Type" },
  { key: "closureRemarks", label: "Closure Remarks" },
  { key: "closedBy", label: "Closed By" },
  { key: "commercialStatus", label: "Commercial Status" },
  { key: "technicalStatus", label: "Technical Status" },
];

/**
 * Grid = exact same columns/data as /reports/domestic (sp_GetDomesticReportData).
 * Excel dump still uses full SR_DETAIL_DUMP_COLUMNS (+ checklist) via dump API.
 */
const DEFAULT_SELECTED_COLUMNS = DOMESTIC_REPORT_COLUMNS.map((c) => c.key);

const REPORT_FETCH_PAGE_SIZE = 10000;
const EXPORT_PAGE_SIZE = 5000;
/** Same default as /reports/domestic — never load unscoped (all-market) data. */
const DEFAULT_MARKET_CODE = "M001";

const ALIGN_CLASS = {
  center: "text-center",
  left: "text-start",
  right: "text-end",
};

const getColumnClassName = (key) => {
  if (String(key || "").startsWith("recommendedTat__")) return "text-end";
  const col = DOMESTIC_REPORT_COLUMNS.find((c) => c.key === key);
  return ALIGN_CLASS[col?.align || "left"] || "text-start";
};

const DATE_KEYS = new Set([
  "raisedDate",
  "lastUpdatedOn",
  "closedDate",
  "invoiceDate",
  "dispatchDate",
  "createdOn",
  "assignedOn",
  "firstResponseDate",
  "closedOn",
  "lastActionDate",
  "lastQueryDate",
]);

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** Grid cell formatter — identical to DomesticReports. */
const formatGridCellValue = (key, value) => {
  if (value == null || value === "") return "";
  if (key === "createdDate") {
    const m = moment(value);
    return m.isValid() ? m.format("DD-MM-YYYY") : String(value);
  }
  if (
    ["claimQty", "claimAmount", "approvedQty", "approvedAmount"].includes(key) ||
    isReportDayColumn(key)
  ) {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    if (isReportDayColumn(key)) {
      return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
    }
    return key.includes("Amount")
      ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : n.toLocaleString("en-IN", { maximumFractionDigits: 3 });
  }
  return String(value);
};

/** Excel dump cell formatter (full dump fields). */
const formatCellValue = (key, value) => {
  if (value == null || value === "") return "";
  if (DATE_KEYS.has(key)) {
    const m = moment(value);
    return m.isValid() ? m.format("DD-MM-YYYY") : String(value);
  }
  if (
    [
      "quantity",
      "srnAmount",
      "srnQuantity",
      "creditNoteAmount",
      "creditNoteQuantity",
      "debitNoteAmount",
      "tat",
      "actualTat",
      "ageing",
      "overallTat",
      "totalQueryCount",
      "totalReassignCount",
      "totalQueries",
      "currentLevel",
    ].includes(key)
  ) {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    if (
      isReportDayColumn(key) ||
      key === "tat" ||
      key === "actualTat" ||
      key === "ageing" ||
      key.includes("Count") ||
      key === "totalQueries" ||
      key === "currentLevel"
    ) {
      return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
    }
    if (key.includes("Amount")) {
      return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
    }
    return n.toLocaleString("en-IN", { maximumFractionDigits: 3 });
  }
  return String(value);
};

const formatRoleWiseTatTooltipHtml = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const parts = text
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length <= 1) return escapeHtml(text);
  return parts.map((part) => escapeHtml(part)).join("<br>");
};

const TOOLTIP_COLUMN_KEYS = new Set([
  "customerName",
  "addressCityState",
  "country",
  "zone",
  "region",
  "territory",
]);

const renderTooltipCell = (key, data, type) => {
  if (type !== "display") return data ?? "";
  const text = formatGridCellValue(key, data);
  if (!text) return "";
  const display = escapeHtml(text);
  const tooltipHtml =
    key === "roleWiseTat" ? formatRoleWiseTatTooltipHtml(text) : display;
  const cellClass =
    key === "roleWiseTat" ? "sr-report-tat-cell" : "sr-report-tooltip-cell";
  return `<span class="${cellClass} sr-report-tip"
    data-bs-toggle="tooltip"
    data-bs-placement="top"
    data-bs-html="true"
    data-bs-title="${tooltipHtml}"
    title="${display}">${display}</span>`;
};

const renderRoleWiseTatCell = (data, type) =>
  renderTooltipCell("roleWiseTat", data, type);

const initReportTooltips = (tableEl) => {
  if (!tableEl || typeof window === "undefined" || !window.bootstrap?.Tooltip) return;
  tableEl.querySelectorAll('[data-bs-toggle="tooltip"].sr-report-tip').forEach((el) => {
    const existing = window.bootstrap.Tooltip.getInstance(el);
    if (existing) existing.dispose();
    new window.bootstrap.Tooltip(el, {
      html: true,
      placement: "top",
      container: "body",
      customClass: "sr-report-cell-tooltip",
      trigger: "hover focus",
    });
  });
};

const dedupeReportRows = (items) => {
  if (!Array.isArray(items) || !items.length) return [];
  const seen = new Set();
  return items.filter((row) => {
    const key = [
      String(row.srCode ?? "").trim(),
      String(row.childSrNumber ?? "").trim(),
      String(row.invoiceNumber ?? "").trim(),
      String(row.complaintSrNumber ?? "").trim(),
    ].join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Same grain as domestic SR Report: parent + child + invoice
const dumpRowKey = (row) =>
  [
    String(row?.srCode ?? "").trim(),
    String(row?.childSrNumber ?? "").trim(),
    String(row?.invoiceNumber ?? "").trim(),
  ].join("|");

const dedupeDumpRows = (items) => {
  if (!Array.isArray(items) || !items.length) return [];
  const seen = new Set();
  return items.filter((row) => {
    const key = dumpRowKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const sanitizeSheetName = (name) => {
  const cleaned = String(name || "Unassigned")
    .replace(/[\\/*?:\[\]]/g, " ")
    .trim()
    .slice(0, 31);
  return cleaned || "Unassigned";
};

const formatChecklistAnswer = (item) => {
  if (!item) return "";
  const text = item.responseText ?? item.ResponseText;
  if (text != null && String(text).trim() !== "") return String(text).trim();
  const val = item.responseValue ?? item.ResponseValue;
  if (val != null && val !== "") return String(val);
  const isBool = item.isBooleanResponse ?? item.IsBooleanResponse;
  if (isBool === true || isBool === 1 || isBool === "1") return "Yes";
  if (isBool === false || isBool === 0 || isBool === "0") return "No";
  return "";
};

const buildChecklistColumns = (checklistResponses) => {
  const list = Array.isArray(checklistResponses) ? checklistResponses : [];
  const byCode = new Map();
  list.forEach((item) => {
    const code = String(item.questionCode ?? item.QuestionCODE ?? "").trim();
    if (!code) return;
    if (byCode.has(code)) return;
    const text = String(item.questionText ?? item.QuestionText ?? code).trim() || code;
    const order = Number(item.displayOrder ?? item.DisplayOrder ?? 0) || 0;
    byCode.set(code, { key: `cl_${code}`, questionCode: code, label: text, order });
  });
  const cols = Array.from(byCode.values()).sort(
    (a, b) => a.order - b.order || a.label.localeCompare(b.label)
  );
  const labelCounts = new Map();
  cols.forEach((c) => {
    const base = c.label;
    const n = (labelCounts.get(base) || 0) + 1;
    labelCounts.set(base, n);
    if (n > 1) c.label = `${base} (${n})`;
  });
  // Fix first duplicate when second appears
  const seenLabels = new Map();
  cols.forEach((c) => {
    const base = String(c.label).replace(/ \(\d+\)$/, "");
    const n = (seenLabels.get(base) || 0) + 1;
    seenLabels.set(base, n);
    if (n === 1 && labelCounts.get(base) > 1) {
      // keep plain for first
      c.label = base;
    } else if (n > 1) {
      c.label = `${base} (${n})`;
    }
  });
  return cols;
};

const buildChecklistAnswerMap = (checklistResponses) => {
  const map = new Map();
  (checklistResponses || []).forEach((item) => {
    const sr = String(item.srCode ?? item.SRCode ?? "").trim();
    const code = String(item.questionCode ?? item.QuestionCODE ?? "").trim();
    if (!sr || !code) return;
    if (!map.has(sr)) map.set(sr, {});
    map.get(sr)[code] = formatChecklistAnswer(item);
  });
  return map;
};

const SEARCH_ICON_SVG = `<svg class="icon-right" xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24" aria-hidden="true" style="position:absolute;right:12px;top:50%;transform:translateY(-50%);pointer-events:none;width:16px;height:16px;color:#6c757d;fill:currentColor;"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>`;

const enhanceGridSearchBox = ($searchEl) => {
  $searchEl.each(function enhanceSearch() {
    const $box = $(this);
    if ($box.data("searchBoxEnhanced")) return;
    $box.data("searchBoxEnhanced", true);
    $box.addClass("srchDvwrpr dt-grid-search");
    const $input = $box.find("input[type='search'], input").first();
    if (!$input.length) return;
    $input
      .removeClass("form-control-sm")
      .addClass("form-control form-control-light text-start")
      .attr("placeholder", $input.attr("placeholder") || "Search...")
      .css({
        height: "42px",
        textAlign: "left",
        paddingRight: "40px",
        minWidth: "280px",
        width: "280px",
      });
    if (!$input.parent().hasClass("input-icon")) {
      $input.wrap(
        '<div class="input-icon" style="position:relative;display:inline-block;margin-left:8px;vertical-align:middle;"></div>'
      );
      $input.parent().append(SEARCH_ICON_SVG);
    }
  });
};

const setupDataTableLayout = (tableEl) => {
  if (!tableEl) return;
  try {
    const wrapper = $(tableEl).closest(".dataTables_wrapper, .dt-container");
    if (!wrapper.length) return;
    const lengthEl = wrapper.find(".dt-length, .dataTables_length");
    const searchEl = wrapper.find(".dt-search, .dataTables_filter");
    if (lengthEl.length || searchEl.length) {
      let topWrap = wrapper.find(".dtFltrTop");
      if (!topWrap.length) {
        topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
        wrapper.prepend(topWrap);
      }
      if (lengthEl.length) topWrap.append(lengthEl);
      if (searchEl.length) {
        topWrap.append(searchEl);
        enhanceGridSearchBox(searchEl);
      }
    }
  } catch (err) {
    console.warn("DataTable layout warning", err);
  }
};

function SrDetailDump() {
  const location = useLocation();
  const navigate = useNavigate();
  const { canView } = getRoutePermissions(location.pathname);
  const canDownload = hasPermission("SR_DETAIL_DUMP", "DOWNLOAD");

  const isSystemAdmin = useMemo(() => isSystemAdminUser(), []);
  const isUnscopedReportViewer = useMemo(
    () => isSystemAdminUser() || isDosAdminUser(),
    []
  );

  const {
    defaultFY,
    filters,
    setFilters,
    filterExpanded,
    setFilterExpanded,
    filterDateRangePickerRef,
    statusOptions,
    srCategoryOptions,
    bvOptions,
    marketOptions,
    countryOptions,
    zoneOptions,
    regionOptions,
    territoryOptions,
    geoLoading,
    isExportCascade,
    exportCountryOptions,
    isGeoAllFilterValue,
    handleGeoFilterChange,
    buildFilterParams,
    handleResetFilters,
    marketDropdownDisabled,
  } = useAdminDashboardFilters({
    defaultMarket: DEFAULT_MARKET_CODE,
    scopeToUser: !isUnscopedReportViewer,
  });

  const tableContainerRef = useRef(null);
  const tableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const initialLoadDoneRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_SELECTED_COLUMNS);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const removedColumnKeysRef = useRef(new Set());

  const allColumns = useMemo(() => buildDomesticReportColumns(rows), [rows]);

  useEffect(() => {
    setSelectedColumns((prev) => {
      const allowed = new Set(allColumns.map((col) => col.key));
      const kept = prev.filter((key) => allowed.has(key));
      const keptSet = new Set(kept);
      allColumns.forEach((col) => {
        if (!keptSet.has(col.key) && !removedColumnKeysRef.current.has(col.key)) {
          keptSet.add(col.key);
        }
      });
      return allColumns.map((col) => col.key).filter((key) => keptSet.has(key));
    });
  }, [allColumns]);

  const visibleColumns = useMemo(
    () => allColumns.filter((col) => selectedColumns.includes(col.key)),
    [allColumns, selectedColumns]
  );

  const tableKey = useMemo(
    () => visibleColumns.map((c) => c.key).join("|"),
    [visibleColumns]
  );

  const destroyDataTable = useCallback(() => {
    const el = tableRef.current;
    if (!el) return;
    try {
      if (typeof window !== "undefined" && window.bootstrap?.Tooltip) {
        el.querySelectorAll('[data-bs-toggle="tooltip"].sr-report-tip').forEach((node) => {
          const instance = window.bootstrap.Tooltip.getInstance(node);
          if (instance) instance.dispose();
        });
      }
      if ($.fn.DataTable && $.fn.DataTable.isDataTable(el)) {
        $(el).DataTable().clear().destroy();
      } else if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.clear().destroy();
      }
    } catch (err) {
      console.warn("DataTable destroy warning", err);
    } finally {
      dataTableInstanceRef.current = null;
      el.innerHTML = "";
    }
  }, []);

  const buildParams = useCallback(
    (overrides = {}) => {
      // Same filter param shape as DomesticReports.buildParams / buildFilterParams
      const params = {
        ...buildFilterParams(overrides),
        pageNumber: overrides.pageNumber ?? 1,
        pageSize: overrides.pageSize ?? REPORT_FETCH_PAGE_SIZE,
      };
      // Always pass market (default Domestic) — never call API without marketCode
      const marketCode = String(
        params.marketCode ||
          overrides.market ||
          filters.market ||
          DEFAULT_MARKET_CODE
      ).trim();
      params.marketCode = marketCode || DEFAULT_MARKET_CODE;
      // Status remains system-admin only; SR Category applies for all user types.
      if (!isSystemAdmin) {
        delete params.statusCode;
      }
      return params;
    },
    [buildFilterParams, isSystemAdmin, filters.market]
  );

  const loadReport = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      setError("");
      try {
        // Grid uses exact same data source as /reports/domestic
        const params = buildParams(overrides);
        const response = await domesticReportAPI.getData(params);
        const payload = response?.data ?? response?.Data ?? {};
        const items = payload.items || payload.Items || [];
        const uniqueItems = dedupeReportRows(items);
        setRows(decorateReportRows(uniqueItems));
        const apiTotal = Number(payload.totalRecords ?? payload.TotalRecords ?? 0) || 0;
        setTotalRecords(
          uniqueItems.length < items.length ? uniqueItems.length : apiTotal || uniqueItems.length
        );
      } catch (e) {
        console.error(e);
        setError("Failed to load report data.");
        setRows([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    if (canView === false) return;
    if (!filters.market) return;
    if (initialLoadDoneRef.current) return;

    // Wait until market options load and current market is valid for this user
    // (avoids first fetch with hardcoded M001 for Export-only assignees).
    if ((marketOptions || []).length > 0) {
      const marketOk = marketOptions.some(
        (o) =>
          String(o?.id || "").trim().toUpperCase() ===
          String(filters.market || "").trim().toUpperCase()
      );
      if (!marketOk) return;
    } else if (!isUnscopedReportViewer) {
      return;
    }

    if (!isUnscopedReportViewer) {
      const hasScopedCountry = (countryOptions || []).some(
        (o) => o?.id && String(o.id).trim() !== "0000"
      );
      const hasScopedZone = (zoneOptions || []).some(
        (o) => o?.id && String(o.id).trim() !== "0000"
      );
      const hasScopedBv = (bvOptions || []).length > 0;
      if (!hasScopedCountry && !hasScopedZone && !hasScopedBv) return;
    }

    initialLoadDoneRef.current = true;
    // Same params as SR Report Filter (market may be Export for Export-only users)
    loadReport({
      market: filters.market || DEFAULT_MARKET_CODE,
      dateRangeStart: filters.dateRangeStart,
      dateRangeEnd: filters.dateRangeEnd,
      bv: Array.isArray(filters.bv) ? [...filters.bv] : [],
      country: Array.isArray(filters.country) ? [...filters.country] : [],
      zone: Array.isArray(filters.zone) ? [...filters.zone] : [],
      region: Array.isArray(filters.region) ? [...filters.region] : [],
      territory: Array.isArray(filters.territory) ? [...filters.territory] : [],
      status: Array.isArray(filters.status) ? [...filters.status] : [],
      complaintCategory: filters.complaintCategory || "",
    });
  }, [
    canView,
    filters.market,
    marketOptions,
    filters.dateRangeStart,
    filters.dateRangeEnd,
    filters.bv,
    filters.country,
    filters.zone,
    filters.region,
    filters.territory,
    filters.status,
    filters.complaintCategory,
    loadReport,
    isUnscopedReportViewer,
    countryOptions,
    zoneOptions,
    bvOptions,
  ]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    if (loading && rows.length === 0) return;
    if (!rows.length) return;

    destroyDataTable();
    container.innerHTML = "";

    const tbl = document.createElement("table");
    tbl.className = "table table-striped table-bordered complaint-table custmTblLst";
    tbl.style.width = "100%";
    container.appendChild(tbl);
    tableRef.current = tbl;

    try {
      if (!$.fn.DataTable) {
        DataTable($);
      }

      const dtColumns = [
        {
          title: "SL No",
          data: null,
          orderable: false,
          searchable: false,
          width: "56px",
          className: "text-center",
          render: (_data, _type, _row, meta) => meta.row + 1,
        },
        ...visibleColumns.map((col) => ({
          title: col.label,
          data: col.key,
          defaultContent: "",
          className: getColumnClassName(col.key),
          width:
            col.key === "roleWiseTat"
              ? "220px"
              : TOOLTIP_COLUMN_KEYS.has(col.key)
              ? "180px"
              : undefined,
          render:
            col.key === "roleWiseTat"
              ? renderRoleWiseTatCell
              : TOOLTIP_COLUMN_KEYS.has(col.key)
              ? (data, type) => renderTooltipCell(col.key, data, type)
              : col.key === "complaintSrNumber"
              ? (_data, type, row) => {
                  if (type !== "display") return _data ?? "";
                  const display = formatGridCellValue("complaintSrNumber", _data);
                  if (!display) return "";
                  const parentSr = row.srCode || row.complaintSrNumber || "";
                  const childSr = row.childSrNumber || "";
                  return `<a href="#" class="sr-report-link" data-parent-sr="${escapeHtml(
                    parentSr
                  )}" data-child-sr="${escapeHtml(childSr)}">${escapeHtml(display)}</a>`;
                }
              : (data) => formatGridCellValue(col.key, data),
        })),
      ];

      dataTableInstanceRef.current = $(tbl).DataTable({
        data: rows,
        columns: dtColumns,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [10, 25, 50, 100, 250, 500],
        dom: "lfrtip",
        ordering: true,
        scrollX: true,
        autoWidth: false,
        deferRender: true,
        pageLength: 50,
        initComplete: function () {
          setupDataTableLayout(tbl);
          initReportTooltips(tbl);
        },
        drawCallback: function () {
          initReportTooltips(tbl);
        },
      });

      $(tbl).on("click", ".sr-report-link", function (e) {
        e.preventDefault();
        const parentSr = $(this).data("parentSr") || "";
        const childSr = $(this).data("childSr") || "";
        if (!parentSr && !childSr) return;
        const parent = parentSr || childSr;
        const childQuery = childSr ? `childSrCode=${encodeURIComponent(childSr)}` : "";
        const fromQuery = "fromList=sr-detail-dump";
        const query = childQuery ? `?${childQuery}&${fromQuery}` : `?${fromQuery}`;
        navigate(`/complaints/${encodeURIComponent(parent)}${query}`);
      });
    } catch (err) {
      console.error("DataTable init error", err);
    }

    return () => {
      destroyDataTable();
      if (container) container.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, visibleColumns, loading, destroyDataTable, tableKey, navigate]);

  const handleFilter = () => {
    // Pass current filter snapshot so BV/geo/market selections match SR Report
    loadReport({
      bv: Array.isArray(filters.bv) ? [...filters.bv] : [],
      country: Array.isArray(filters.country) ? [...filters.country] : [],
      zone: Array.isArray(filters.zone) ? [...filters.zone] : [],
      region: Array.isArray(filters.region) ? [...filters.region] : [],
      territory: Array.isArray(filters.territory) ? [...filters.territory] : [],
      market: filters.market || DEFAULT_MARKET_CODE,
      status: Array.isArray(filters.status) ? [...filters.status] : [],
      complaintCategory: filters.complaintCategory || "",
      dateRangeStart: filters.dateRangeStart,
      dateRangeEnd: filters.dateRangeEnd,
    });
  };

  const handleReset = () => {
    const resetParams = handleResetFilters();
    setSelectedColumns(DEFAULT_SELECTED_COLUMNS);
    loadReport({
      ...resetParams,
      market: resetParams?.market || DEFAULT_MARKET_CODE,
    });
  };

  const fetchAllExportPages = async () => {
    const allItems = [];
    const allChecklist = [];
    let pageNumber = 1;
    let total = Infinity;

    while (allItems.length < total) {
      const params = buildParams({
        pageNumber,
        pageSize: EXPORT_PAGE_SIZE,
        market: filters.market || DEFAULT_MARKET_CODE,
        dateRangeStart: filters.dateRangeStart,
        dateRangeEnd: filters.dateRangeEnd,
        bv: Array.isArray(filters.bv) ? [...filters.bv] : [],
        country: Array.isArray(filters.country) ? [...filters.country] : [],
        zone: Array.isArray(filters.zone) ? [...filters.zone] : [],
        region: Array.isArray(filters.region) ? [...filters.region] : [],
        territory: Array.isArray(filters.territory) ? [...filters.territory] : [],
        status: Array.isArray(filters.status) ? [...filters.status] : [],
        complaintCategory: filters.complaintCategory || "",
      });
      const response = await srDetailDumpAPI.getData(params);
      const payload = response?.data ?? response?.Data ?? {};
      const items = payload.items || payload.Items || [];
      const checklist = payload.checklistResponses || payload.ChecklistResponses || [];
      total = Number(payload.totalRecords ?? payload.TotalRecords ?? items.length) || items.length;
      allItems.push(...items);
      allChecklist.push(...checklist);
      if (!items.length || items.length < EXPORT_PAGE_SIZE) break;
      pageNumber += 1;
      if (pageNumber > 200) break;
    }

    return {
      items: dedupeDumpRows(allItems),
      checklistResponses: allChecklist,
    };
  };

  const marketLabel = useMemo(() => {
    const raw =
      marketOptions.find((m) => m.id === filters.market)?.text ||
      (String(filters.market || "").toUpperCase() === "M001"
        ? "Domestic"
        : String(filters.market || "").toUpperCase() === "M002"
          ? "Export"
          : filters.market) ||
      "All";
    return String(raw).trim() || "All";
  }, [marketOptions, filters.market]);

  const buildExportFileName = (extension) => {
    const marketTag =
      marketLabel.replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, "_") || "All";
    return `SR_Detail_Dump_${marketTag}_${moment().format("YYYYMMDD_HHmmss")}.${extension}`;
  };

  const triggerBlobDownload = (blob, fileName) => {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Fetches every page, then honours the grid's search box: when one is active
  // only the rows the user can see are exported, otherwise the whole data set.
  const resolveExportData = async () => {
    const { items: fetchedItems, checklistResponses } = await fetchAllExportPages();
    const isFiltered = isDataTableFiltered(dataTableInstanceRef);
    let items = fetchedItems;
    if (isFiltered) {
      const visibleKeys = new Set(
        getDataTableExportRows(dataTableInstanceRef, rows).map(dumpRowKey)
      );
      items = fetchedItems.filter((row) => visibleKeys.has(dumpRowKey(row)));
    }
    const checklistCols = buildChecklistColumns(checklistResponses);
    const answerMap = buildChecklistAnswerMap(checklistResponses);
    const decorated = decorateReportRows(items);
    const recommended = collectRecommendedTatColumns(decorated);
    const recommendedCols = recommended.length
      ? recommended
      : [{ key: "tat", label: "Recommended TAT" }];
    const actualIndex = SR_DETAIL_DUMP_COLUMNS.findIndex((col) => col.key === "actualTat");
    const dumpColumns =
      actualIndex < 0
        ? [...SR_DETAIL_DUMP_COLUMNS, ...recommendedCols]
        : [
            ...SR_DETAIL_DUMP_COLUMNS.slice(0, actualIndex),
            ...recommendedCols,
            ...SR_DETAIL_DUMP_COLUMNS.slice(actualIndex),
          ];
    return {
      items: decorated,
      isFiltered,
      searchTerm: getDataTableSearchTerm(dataTableInstanceRef),
      answerMap,
      exportColumns: [...dumpColumns, ...checklistCols],
    };
  };

  const exportCellValue = (row, col, answerMap) => {
    if (col.questionCode) {
      const srAnswers = answerMap.get(String(row.srCode || "").trim()) || {};
      return srAnswers[col.questionCode] ?? "";
    }
    return formatCellValue(col.key, row[col.key]);
  };

  const buildFilterNote = ({ isFiltered, searchTerm }, count) => {
    if (!isFiltered) return "";
    return searchTerm
      ? `Filtered by search: "${searchTerm}" (${count} records)`
      : `Filtered view (${count} records)`;
  };

  // Shared plumbing for the three export formats: resolve the rows once, then
  // hand them to the writer while keeping the busy state and errors consistent.
  const runExport = async (writer) => {
    setDownloading(true);
    setError("");
    try {
      const exportData = await resolveExportData();
      await writer(exportData);
    } catch (e) {
      console.error(e);
      setError("Failed to download SR detail dump.");
    } finally {
      setDownloading(false);
    }
  };

  const exportToExcel = () =>
    runExport(async (exportData) => {
      const { items, answerMap, exportColumns } = exportData;

      // One sheet per business vertical, as before.
      const byBv = new Map();
      (items || []).forEach((row) => {
        const bv = String(row.businessVertical || "").trim() || "Unassigned";
        if (!byBv.has(bv)) byBv.set(bv, []);
        byBv.get(bv).push(row);
      });

      const workbook = new ExcelJS.Workbook();
      const usedSheetNames = new Set();
      const sortedBvs = Array.from(byBv.keys()).sort((a, b) => a.localeCompare(b));

      if (!sortedBvs.length) {
        sortedBvs.push("Unassigned");
        byBv.set("Unassigned", []);
      }

      const filterNote = buildFilterNote(exportData, items.length);

      sortedBvs.forEach((bv) => {
        let sheetName = sanitizeSheetName(bv);
        let suffix = 2;
        while (usedSheetNames.has(sheetName.toLowerCase())) {
          const base = sanitizeSheetName(bv).slice(0, 28);
          sheetName = sanitizeSheetName(`${base}_${suffix}`);
          suffix += 1;
        }
        usedSheetNames.add(sheetName.toLowerCase());

        const sheet = workbook.addWorksheet(sheetName);
        const colCount = Math.max(exportColumns.length, 1);
        sheet.mergeCells(1, 1, 1, colCount);
        sheet.getCell(1, 1).value = `SR Detail Dump — ${bv} (${marketLabel})`;
        sheet.getCell(1, 1).font = { bold: true, size: 14 };

        if (filterNote) {
          sheet.mergeCells(2, 1, 2, colCount);
          sheet.getCell(2, 1).value = filterNote;
          sheet.getCell(2, 1).font = {
            italic: true,
            size: 11,
            color: { argb: "FF666666" },
          };
        }

        const headerRow = sheet.getRow(3);
        headerRow.values = exportColumns.map((c) => c.label);
        headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
        headerRow.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF198754" },
        };

        (byBv.get(bv) || []).forEach((row) => {
          sheet.addRow(exportColumns.map((c) => exportCellValue(row, c, answerMap)));
        });

        exportColumns.forEach((_, idx) => {
          sheet.getColumn(idx + 1).width = 18;
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      triggerBlobDownload(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        buildExportFileName("xlsx")
      );
    });

  const exportToCSV = () =>
    runExport(async ({ items, answerMap, exportColumns }) => {
      // CSV has no sheets, so every business vertical lands in one flat file.
      const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
      const lines = [
        exportColumns.map((c) => escape(c.label)).join(","),
        ...items.map((row) =>
          exportColumns.map((c) => escape(exportCellValue(row, c, answerMap))).join(",")
        ),
      ];
      triggerBlobDownload(
        new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" }),
        buildExportFileName("csv")
      );
    });

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportOption = (format) => {
    handleExportMenuClose();
    if (format === "excel") {
      exportToExcel();
    } else if (format === "csv") {
      exportToCSV();
    }
  };

  if (canView === false) {
    return (
      <Box className="domestic-reports-page">
        <div className="alert alert-warning mb-0">
          You do not have permission to view SR Detail Dump.
        </div>
      </Box>
    );
  }

  return (
    <Box className="domestic-reports-page">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          className="service-request-heading"
          sx={{ color: "#333333", fontWeight: 600 }}
        >
          SR Detail Dump
        </Typography>
        {canDownload && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* Export Button with Dropdown */}
            <Button
              onClick={handleExportMenuOpen}
              disabled={downloading || loading}
              sx={{
                background: "transparent",
                border: "1px solid #667eea",
                color: "#667eea",
                fontWeight: 500,
                padding: "8px 10px",
                borderRadius: "6px",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "36px",
                height: "36px",
                "&:hover": {
                  background: "transparent",
                  borderColor: "#764ba2",
                  color: "#764ba2",
                },
                "& .MuiSvgIcon-root": {
                  color: "#667eea",
                },
                "&:hover .MuiSvgIcon-root": {
                  color: "#764ba2",
                },
              }}
              title={downloading ? "Downloading..." : "Export"}
            >
              <FileDownloadIcon
                sx={{ fontSize: "26px", filter: "drop-shadow(0 0 0.5px currentColor)" }}
              />
              <ArrowDropDownIcon sx={{ fontSize: "16px", ml: 0.5 }} />
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={handleExportMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem onClick={() => handleExportOption("excel")}>Excel</MenuItem>
              <MenuItem onClick={() => handleExportOption("csv")}>CSV</MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

      <div
        className="card-panel mb-3 filter-panel"
        style={{ paddingBottom: filterExpanded ? "15px" : "2px" }}
      >
        <div
          className={`section-heading d-flex align-items-center gap-2 ${
            filterExpanded ? "mb-3" : "filter-panel-heading-collapsed"
          }`}
          style={{
            marginBottom: filterExpanded ? "14px" : 0,
            cursor: "pointer",
          }}
          onClick={() => setFilterExpanded(!filterExpanded)}
        >
          <FilterListIcon
            fontSize="small"
            className="text-muted"
            style={{ width: "18px", height: "18px", flexShrink: 0 }}
          />
          <span>Filter</span>
          <span className="filter-panel-active-daterange ms-auto">
            {filters.dateRange || defaultFY.dateRange || "Select date range"}
          </span>
          {filterExpanded ? (
            <ExpandLessIcon fontSize="small" className="text-muted" />
          ) : (
            <ExpandMoreIcon fontSize="small" className="text-muted" />
          )}
        </div>

        {filterExpanded && (
          <div className="container-fluid">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-6 col-lg-3">
                <label className="input-label d-block mb-1 text-start">
                  Select Date Range
                </label>
                <div className="input-icon" style={{ position: "relative" }}>
                  <input
                    type="text"
                    ref={filterDateRangePickerRef}
                    className="form-control form-control-light text-start"
                    placeholder="Select date range"
                    readOnly
                    style={{ height: "42px", textAlign: "left" }}
                  />
                  <CalendarTodayIcon
                    className="icon-right"
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      width: "16px",
                      height: "16px",
                      color: "#6c757d",
                    }}
                  />
                </div>
              </div>

              {isSystemAdmin && (
                <div className="col-12 col-md-6 col-lg-3">
                  <label className="input-label d-block mb-1 text-start">Status</label>
                  <CustomMultiSelect
                    options={statusOptions}
                    value={filters.status || []}
                    onChange={(selected) =>
                      setFilters((prev) => ({
                        ...prev,
                        status: Array.isArray(selected) ? selected : [],
                      }))
                    }
                    placeholder="Select Status"
                  />
                </div>
              )}

              <div className="col-12 col-md-6 col-lg-3">
                <label className="input-label d-block mb-1 text-start">Market</label>
                <select
                  className="form-select form-control-light filter-select text-start"
                  value={filters.market || DEFAULT_MARKET_CODE}
                  disabled={!marketOptions.length || marketDropdownDisabled}
                  onChange={(e) => {
                    // Keep Domestic as fallback — never leave market empty (would return all markets)
                    const nextMarket = e.target.value || DEFAULT_MARKET_CODE;
                    setFilters((prev) => ({
                      ...prev,
                      market: nextMarket,
                      complaintCategory: "",
                      country: [],
                      zone: [],
                      region: [],
                      territory: [],
                      bv: [],
                    }));
                  }}
                  style={{ height: "42px", textAlign: "left" }}
                >
                  {marketOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.text}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="input-label d-block mb-1 text-start">
                  Business Vertical
                </label>
                <CustomMultiSelect
                  options={bvOptions}
                  value={filters.bv || []}
                  onChange={(selected) =>
                    setFilters((prev) => ({
                      ...prev,
                      bv: Array.isArray(selected) ? selected : [],
                    }))
                  }
                  placeholder="Select Business Vertical"
                />
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="input-label d-block mb-1 text-start">SR Category</label>
                <select
                  key={`sr-cat-${filters.market || "none"}`}
                  className="form-select form-control-light filter-select text-start"
                  style={{ height: "42px", textAlign: "left" }}
                  disabled={!filters.market}
                  value={filters.complaintCategory || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      complaintCategory: e.target.value || "",
                    }))
                  }
                >
                  <option value="">
                    {!filters.market ? "Select Market first" : "Select SR Category"}
                  </option>
                  {srCategoryOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.text}
                    </option>
                  ))}
                </select>
              </div>

              {(isExportCascade
                ? ["zone", "country", "region", "territory"]
                : ["country", "zone", "region", "territory"]
              ).map((geoKey) => {
                const countryIsAll = isGeoAllFilterValue(filters.country);
                const zoneIsAll = isGeoAllFilterValue(filters.zone);
                const regionIsAll = isGeoAllFilterValue(filters.region);
                if (geoKey === "country") {
                  const countryList = isExportCascade
                    ? exportCountryOptions
                    : countryOptions;
                  return (
                    <div key="country" className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">Country</label>
                      <CustomMultiSelect
                        options={countryList}
                        value={filters.country || []}
                        onChange={(selected) =>
                          handleGeoFilterChange("country", selected, countryList)
                        }
                        placeholder={
                          isExportCascade
                            ? filters.zone?.length
                              ? "Select Country"
                              : "Select zone first"
                            : "Select Country"
                        }
                        disabled={
                          !filters.market ||
                          (isExportCascade && (!filters.zone?.length || zoneIsAll))
                        }
                        loading={geoLoading.country}
                        loadingText="Loading countries..."
                        showSelectAll={!countryIsAll && !zoneIsAll}
                      />
                    </div>
                  );
                }
                if (geoKey === "zone") {
                  return (
                    <div key="zone" className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">Zone</label>
                      <CustomMultiSelect
                        options={zoneOptions}
                        value={filters.zone || []}
                        onChange={(selected) =>
                          handleGeoFilterChange("zone", selected, zoneOptions)
                        }
                        placeholder={
                          !filters.market
                            ? "Select market first"
                            : isExportCascade
                              ? "Select zone(s)"
                              : filters.country?.length
                                ? "Select zone(s)"
                                : "Select country first"
                        }
                        disabled={
                          !filters.market ||
                          (!isExportCascade &&
                            (!filters.country?.length || countryIsAll))
                        }
                        loading={geoLoading.zone}
                        loadingText="Loading zones..."
                        showSelectAll={!zoneIsAll && !countryIsAll}
                      />
                    </div>
                  );
                }
                if (geoKey === "region") {
                  const regionReady = isExportCascade
                    ? filters.zone?.length && filters.country?.length
                    : filters.zone?.length;
                  const lockedByAll = countryIsAll || zoneIsAll || regionIsAll;
                  return (
                    <div key="region" className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">Region</label>
                      <CustomMultiSelect
                        options={regionOptions}
                        value={filters.region || []}
                        onChange={(selected) =>
                          handleGeoFilterChange("region", selected, regionOptions)
                        }
                        placeholder={
                          regionReady
                            ? "Select region(s)"
                            : isExportCascade
                              ? "Select country first"
                              : "Select zone first"
                        }
                        disabled={!filters.market || !regionReady || lockedByAll}
                        loading={geoLoading.region}
                        loadingText="Loading regions..."
                        showSelectAll={!lockedByAll}
                      />
                    </div>
                  );
                }
                const territoryLockedByAll = countryIsAll || zoneIsAll || regionIsAll;
                return (
                  <div key="territory" className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">Territory</label>
                    <CustomMultiSelect
                      options={territoryOptions}
                      value={filters.territory || []}
                      onChange={(selected) =>
                        handleGeoFilterChange("territory", selected, territoryOptions)
                      }
                      placeholder={
                        filters.region?.length
                          ? "Select territory(ies)"
                          : "Select region first"
                      }
                      disabled={
                        !filters.market ||
                        !filters.region?.length ||
                        territoryLockedByAll
                      }
                      loading={geoLoading.territory}
                      loadingText="Loading territories..."
                      showSelectAll={!territoryLockedByAll}
                    />
                  </div>
                );
              })}

              <div className="col-12 col-md-6 col-lg-3">
                <label className="input-label d-block mb-1 text-start">
                  <ViewColumnIcon fontSize="inherit" className="me-1" />
                  Report Columns
                </label>
                <CustomMultiSelect
                  options={allColumns.map((c) => ({
                    id: c.key,
                    text: c.label,
                  }))}
                  value={selectedColumns}
                  onChange={(selected) => {
                    const next = Array.isArray(selected) ? selected : [];
                    const chosen = next.length ? next : allColumns.map((col) => col.key);
                    const chosenSet = new Set(chosen);
                    removedColumnKeysRef.current = new Set(
                      allColumns.map((col) => col.key).filter((key) => !chosenSet.has(key))
                    );
                    setSelectedColumns(
                      allColumns.map((col) => col.key).filter((key) => chosenSet.has(key))
                    );
                  }}
                  placeholder="Select columns"
                  showSelectAll
                />
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label
                  className="input-label d-block mb-1"
                  style={{ visibility: "hidden" }}
                >
                  Actions
                </label>
                <div className="d-flex gap-2 filter-buttons align-items-center">
                  <button
                    type="button"
                    className="btn btn-filter btn-sm"
                    onClick={handleFilter}
                    style={{
                      background: "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                      border: "none",
                      color: "white",
                      fontWeight: 600,
                      boxShadow: "0 2px 8px rgba(102, 126, 234, 0.4)",
                      borderRadius: "4px",
                      padding: "4px 12px",
                      fontSize: "0.875rem",
                    }}
                  >
                    <FilterListIcon fontSize="small" className="me-1" />
                    Filter
                  </button>
                  <button
                    type="button"
                    className="btn btn-reset-filter btn-sm"
                    onClick={handleReset}
                    title="Reset"
                    style={{
                      background: "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                      border: "none",
                      color: "white",
                      fontWeight: 600,
                      boxShadow: "0 2px 8px rgba(132, 250, 176, 0.4)",
                      borderRadius: "4px",
                      padding: "4px 10px",
                      fontSize: "0.875rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel domestic-reports-grid-panel mt-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
            <Typography
              variant="body2"
              component="div"
              className="section-heading mb-0"
              sx={{ fontSize: "14px", fontWeight: 600 }}
            >
              Report Data
              <span className="text-muted ms-2" style={{ fontSize: "12px", fontWeight: 500 }}>
                ({totalRecords.toLocaleString("en-IN")} records · {visibleColumns.length}{" "}
                columns)
              </span>
            </Typography>
          </div>

          {loading && rows.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ fontSize: "14px" }}>Loading report data...</Typography>
            </Box>
          ) : !loading && rows.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: "14px" }}>
                No records found for the selected filters. Adjust filters and click Filter.
              </Typography>
            </Box>
          ) : (
            <div
              className="table-responsive domestic-reports-table-wrap"
              ref={tableContainerRef}
            />
          )}
        </div>
      </Paper>
    </Box>
  );
}

export default SrDetailDump;
